import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { PhonicsSound, useGetPhonicsSoundsQuery } from '@/api/types';
import { Lines } from '@/types';
import { useUi } from '@/context/Ui';
import { findSoundsInString } from '@/utils/sounds';
import { useAssetCdn } from '@/context/Ui';
import { SymbolType, CdnObjectType } from '@/types';
import { CanvasTrace, AudioPlayer } from '@/components';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { Toolbar } from '../components/Toolbar';
import { WhitePanel } from '../components/WhitePanel';
import { Activity, ActivityStep } from '../Activity';
import { ActivityTypeProps } from '../types';

interface TraceFocusSoundProps {
  sound: string;
}

interface TraceFocusSound {
  asBitmap: () => string | undefined;
  asVector: () => Lines | undefined;
}

const TraceFocusSound = forwardRef<TraceFocusSound, TraceFocusSoundProps>(
  ({ sound }, ref) => {
    const playerRef = useRef<AudioPlayer>(null);
    const traceRef = useRef<CanvasTrace>(null);

    const { sendAlert } = useUi();

    useImperativeHandle(ref, () => ({
      asBitmap: () => traceRef.current?.asBitmap(),
      asVector: () => traceRef.current?.asVector(),
    }));

    const mediaUrl = useAssetCdn(sound, SymbolType.Sound, CdnObjectType.Audio);

    const [playedOnce, setPlayedOnce] = useState(false);

    const [playerReady, setPlayerReady] = useState(false);

    const [, setScore] = useState<number>(0);

    const [soundObject, setSoundObject] = useState<PhonicsSound | undefined>(
      undefined,
    );

    const { data: soundsData } = useGetPhonicsSoundsQuery();

    useEffect(() => {
      if (!soundsData) {
        return;
      }

      setSoundObject(soundsData.getPhonicsSounds.find((s) => s.name === sound));
    }, [soundsData, sound]);

    return (
      <>
        <Toolbar
          onPlay={() => {
            playerRef.current?.play();
            setPlayedOnce(true);
          }}
          hintPlay={!playedOnce}
          playDisabled={!playerReady}
          onRefresh={() => traceRef.current?.reset()}
        />

        <WhitePanel mode="basic" kind="fill" color="paper">
          {soundObject ? (
            <CanvasTrace
              ref={traceRef}
              className="w-72 h-72 mx-auto bg-blue-50"
              drawWidth={288}
              drawHeight={288}
              traceBasis={soundObject.referencePath || undefined}
              showBasis={false}
              traceGuide={soundObject.guidePath || undefined}
              showGuide={false}
              showScore={false}
              onScoreChange={setScore}
            />
          ) : (
            <p>Unable to build trace object</p>
          )}
        </WhitePanel>

        <AudioPlayer
          ref={playerRef}
          src={mediaUrl || ''}
          onReadySrc={() => setPlayerReady(true)}
          onLoadTimeoutSrc={() =>
            sendAlert('We could not load some of the audio for this lesson.', {
              type: 'warning',
              title: 'There was a problem',
            })
          }
        />
      </>
    );
  },
);

TraceFocusSound.displayName = 'TraceFocusSound';

interface SpellingFocusSoundsStepProps extends ActivityTypeProps {
  index: number;
  total: number;
  sound: string;
}

const SpellingFocusSoundsStep = ({
  activity,
  mode,
  wrapperClassName,
  index,
  total,
  sound,
}: SpellingFocusSoundsStepProps) => {
  const traceFocusSoundRef = useRef<TraceFocusSound | null>(null);

  return (
    <ActivityStep
      key={`${activity.id}-${index}-1`}
      stepIdentifier={`${activity.id}-${index}-1`}
      activity={activity}
      mode={mode}
      stepNumber={index + 1}
      totalSteps={total}
      wrapperClassName={wrapperClassName}
      usePanel
      showTitle={false}
      showActivityPagination={false}
      getActivityResponseMetaData={() => {
        return {
          text: sound,
          base64Image: traceFocusSoundRef.current?.asBitmap(),
          points: JSON.stringify(traceFocusSoundRef.current?.asVector()),
        };
      }}
    >
      <TraceFocusSound ref={traceFocusSoundRef} sound={sound} />
    </ActivityStep>
  );
};

export const SpellingFocusSounds = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.sounds) {
    throw new Error('Sounds are required for this activity');
  }

  const { data: soundsData } = useGetPhonicsSoundsQuery();

  const sounds = findSoundsInString(
    soundsData?.getPhonicsSounds.map((sound) => sound.name) || [],
    activity.sounds,
  );

  return (
    <Activity
      steps={[
        ...(mode === 'TEACH'
          ? [
              <ActivityTitleStep
                key={`${activity.id}-title`}
                activity={activity}
                mode={mode}
                wrapperClassName={wrapperClassName}
                isPreview={isPreview}
                index={0}
                total={sounds.length + 1}
              />,
            ]
          : []),
        ...sounds.flatMap((sound, index) => [
          <SpellingFocusSoundsStep
            key={`${activity.id}-${index}-1`}
            activity={activity}
            mode={mode}
            wrapperClassName={wrapperClassName}
            isPreview={isPreview}
            index={index + 1}
            total={sounds.length + 1}
            sound={sound}
          />,
        ]),
      ]}
    />
  );
};
